import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { uniq } from 'lodash';

import {
  Box,
  CircularProgress,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';

import { Warning } from '@mui/icons-material';
import { ResponsiveContainer } from 'recharts';
import { AxisDomain, AxisDomainItem } from 'recharts/types/util/types';

import { trpc } from '../../api/trpc';

import { useFormatNumber } from '../../utils/format-number';
import { ChartDataVariableId } from '../../utils/statistics';

import { useVisibility, VisibilityProps } from '../../store/use-visibility';

import {
  Mode,
  useChartBoxModeSelect,
  Variant,
  Aggregate,
} from './chart-box.use-mode-select';
import {
  statisticsConfig,
  StatisticsUnit,
} from '../../../../core/src/shared/statistics-config';
import { Row } from '../../atoms/row';

import { getBarChart } from './chart-box.bar-chart';
import { getAreaChart } from './chart-box.area-chart';

export type ChartBoxProps = {
  variableId: ChartDataVariableId;
  variableSuffix?: string;
  yAxisDomainEndFrom?: ChartDataVariableId | ChartDataVariableId[];
  initialVariant?: Variant;
  getSubStatsLabel?: (key: string) => string;
  groupSubStatsByInverter?: boolean;
  options?: {
    mode: Mode;
    from: Date;
    to: Date;
    variant: Variant;
  };
} & VisibilityProps;

export const ChartBox = ({
  variableId,
  variableSuffix,
  yAxisDomainEndFrom,
  getSubStatsLabel,
  groupSubStatsByInverter,
  visibility,
  initialVariant,
  options: propsOptions,
}: ChartBoxProps) => {
  const confirm = useConfirm();

  const { filters, footer, ...options } = useChartBoxModeSelect({
    variableId,
    parent: false,
    initialVariant,
  });

  const { mode, from, to, variant } = useMemo(() => {
    return propsOptions ?? options;
  }, [options, propsOptions]);

  const { formatDate, formatTime } = useIntl();
  const formatNumber = useFormatNumber();

  const { unit } = statisticsConfig[variableId];

  const mobile = useMediaQuery(({ breakpoints }: Theme) =>
    breakpoints.down('sm')
  );

  const visible = useVisibility(visibility);

  let aggregate = Aggregate.Disabled;
  if (mode === Mode.Year) {
    aggregate = Aggregate.Hour;
  }

  if ([Variant.Bar, Variant.BarStacked].includes(variant)) {
    if (mode === Mode.Day) {
      aggregate = Aggregate.Hour;
    }
    if (mode === Mode.Week) {
      aggregate = Aggregate.Day;
    }
    if (mode === Mode.Year) {
      aggregate = Aggregate.Month;
    }
  }

  const {
    data: rawData,
    isLoading,
    isError,
    error,
  } = trpc.getStats.useQuery(
    {
      variableId,
      variableSuffix,
      aggregate,
      yAxisDomainEndFrom,
      from: from.toISOString(),
      to: to.toISOString(),
      includeSubStats: [Variant.BarStacked, Variant.LineStacked].includes(
        variant
      ),
    },
    { enabled: visible, networkMode: 'always' }
  );

  const data = useMemo(() => {
    if (!rawData || !groupSubStatsByInverter) return rawData;

    const subStatsKeys = uniq(rawData.subStatsKeys.map(stripStringIndex));

    const sortedData = rawData.sortedData.map((point) => ({
      ...point,
      subStats: Object.entries(point.subStats).reduce<Record<string, number>>(
        (acc, [key, value]) => {
          const grouped = stripStringIndex(key);
          acc[grouped] = (acc[grouped] ?? 0) + (value ?? 0);
          return acc;
        },
        {}
      ),
    }));

    return { ...rawData, subStatsKeys, sortedData };
  }, [rawData, groupSubStatsByInverter]);

  const yAxisDomain: AxisDomain = useMemo(() => {
    let min: AxisDomainItem = 'auto';
    let max: AxisDomainItem =
      typeof data?.yAxisDomainMax === 'number' ? data?.yAxisDomainMax : 'auto';

    if (unit === StatisticsUnit.Percent) {
      min = 0;
      max = 100;
    }

    return [min, max];
  }, [data, unit]);

  const yAxisTickFormatter = (at: any) => {
    if (mode === Mode.Day) return formatTime(new Date(at));
    if (mode === Mode.Week) {
      return formatDate(new Date(at), { weekday: 'short' });
    }
    if (mode === Mode.Year) {
      return formatDate(new Date(at), { month: 'short' });
    }
    return '';
  };

  const maxValue = useMemo(() => {
    if (yAxisDomain && typeof yAxisDomain[1] === 'number') {
      return yAxisDomain[1];
    } else if (data) {
      return Math.max(...data.sortedData.map((item) => item.value));
    }

    return undefined;
  }, [data, yAxisDomain]);

  if (!visible) return null;

  return (
    <Paper sx={{ p: 0, pt: 2, width: '100%', position: 'relative' }}>
      {!propsOptions && <Box sx={{ pb: 2, px: 2 }}>{filters}</Box>}

      <Box
        sx={{
          pt: 0,
          pb: 2,
          px: 2,
          height: 340,
          width: '100%',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          {data &&
          (variant === Variant.Line || variant === Variant.LineStacked) ? (
            getAreaChart({
              data,
              mode,
              unit,
              yAxisDomain,
              yAxisTickFormatter,
              mobile,
              formatNumber,
              maxValue,
              variant,
              variableId,
              getSubStatsLabel,
            })
          ) : data &&
            (variant === Variant.Bar || variant === Variant.BarStacked) ? (
            getBarChart({
              data,
              mode,
              unit,
              yAxisDomain,
              yAxisTickFormatter,
              mobile,
              formatNumber,
              maxValue,
              variant,
              variableId,
              getSubStatsLabel,
            })
          ) : (
            <></>
          )}
        </ResponsiveContainer>
      </Box>

      <Row sx={{ pb: 1.5, px: 2 }}>
        {!propsOptions && footer}

        {typeof data?.summary === 'number' && (
          <Typography variant="body2" sx={{ ml: 'auto', textAlign: 'right' }}>
            <FormattedMessage
              id="charts.summary.value"
              defaultMessage="Celkem {value}"
              values={{
                value: formatNumber(
                  mode === Mode.Year ? data.summary / 1000 : data.summary,
                  {
                    style: 'unit',
                    unit:
                      mode === Mode.Year ? 'megawatt-hour' : 'kilowatt-hour',
                    maximumFractionDigits: mode === Mode.Year ? 2 : 1,
                    minimumFractionDigits: mode === Mode.Year ? 2 : 1,
                  }
                ),
              }}
            />
          </Typography>
        )}
      </Row>

      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: isLoading || isError ? 'flex' : 'none',
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: isLoading ? 'none' : 'auto',
        }}
        onClick={() => {
          if (isError) {
            confirm({
              title: (
                <FormattedMessage
                  id="charts.error.dialog.title"
                  defaultMessage="Při načítání statistik došlo k chybě"
                />
              ),
              description: (
                <>
                  <pre>{JSON.stringify(error)}</pre>
                </>
              ),
            });
          }
        }}
      >
        {isLoading && (
          <CircularProgress
            size={22}
            sx={{ animationDuration: '850ms', display: 'block' }}
          />
        )}

        {isError && <Warning sx={{ fontSize: 22, color: 'warning.main' }} />}
      </Box>
    </Paper>
  );
};

const stripStringIndex = (key: string) => {
  const lastDash = key.lastIndexOf('-');
  return lastDash === -1 ? key : key.slice(0, lastDash);
};
